import { ProductGroup } from '@@partner:types:app'

import './ProductCarousel.scss'

interface ProductCarouselData {
  mode: 'manual' | 'group';
  xids: string[];            // Список артикулов, если выбрана ручная настройка
  productGroupId?: string;   // ID группы товаров, если выбран режим group
  title?: string;            // Заголовок блока
}

interface ProductCarouselConfig {
  groupList?: ProductGroup[];
  onViewGroup?: (groupId: string) => void; 
  // callback, который дернется при нажатии "Посмотреть группу"
}

interface EditorJSAPI {
  blocks: {
    update: (id: string, data: any) => void;
  };
}

class ProductCarousel {
  // Храним состояние блока
  private data: ProductCarouselData;
  private wrapper: HTMLElement | null;
  private blockId: string; // Уникальный ID блока
  private api: EditorJSAPI; // API Editor.js

  /**
   * Список групп, которые вы передаёте через config.groupList
   */

  private groupList: ProductGroup[];

  private onViewGroup?: (groupId: string) => void;

  static get toolbox () {
    return {
      title: 'Список товаров',
      icon: '🛍️',
    }
  }

  constructor ({
    data,
    config,
    block,
    api,
  }: {
    data?: ProductCarouselData;
    config?: ProductCarouselConfig;
    block: { id: string };
    api: EditorJSAPI;
  }) {
    // Значения по умолчанию
    this.data = data ?? {
      mode: 'manual',
      xids: [],
      productGroupId: undefined,
      title: '',
    }

    // Получаем список групп из config
    this.groupList = config?.groupList || []
    this.onViewGroup = config?.onViewGroup
    this.blockId = block.id // Сохраняем уникальный ID блока
    this.api = api // Сохраняем API

    this.wrapper = null
  }

  
  /**
   * Рендер DOM-элементов блока
   */

  render (): HTMLElement {
    this.wrapper = document.createElement('div')
    this.wrapper.classList.add('product-carousel-tool')

    // Панель управления (radio-селектор для mode, поля для XIDs или выбора группы)
    const controls = this.renderControls()
    this.wrapper.appendChild(controls)

    return this.wrapper
  }

  /**
   * Панель управления (выбор режима, ввод xids или groupId)
   */

  private renderControls (): HTMLElement {
    const controls = document.createElement('div')
    controls.classList.add('pc-controls')

    // 0. Поле заголовка
    const titleLabel = document.createElement('label')
    titleLabel.textContent = 'Заголовок блока: '

    const titleInput = document.createElement('input')
    titleInput.type = 'text'
    titleInput.value = this.data.title || ''
    titleInput.classList.add(`pc-title-input-${this.blockId}`)
    titleInput.placeholder = 'Введите заголовок (необязательно)'

    titleInput.addEventListener('input', () => {
      this.data.title = titleInput.value
      this.triggerChange()
    })

    titleLabel.appendChild(titleInput)
    controls.appendChild(titleLabel)

    // 1. Радио-кнопки с уникальными именами
    const radioName = `pc_mode_${this.blockId}` // Уникальное имя для каждого блока
    
    const manualRadio = document.createElement('input')
    manualRadio.type = 'radio'
    manualRadio.name = radioName
    manualRadio.value = 'manual'
    manualRadio.checked = this.data.mode === 'manual'

    manualRadio.addEventListener('change', () => {
      if (manualRadio.checked) {
        this.data.mode = 'manual'
        // НЕ сбрасываем groupId, просто меняем режим
        this.refreshControls(controls)
      }
    })

    const manualLabel = document.createElement('label')
    manualLabel.textContent = 'Артикулы товаров'
    manualLabel.prepend(manualRadio)

    const groupRadio = document.createElement('input')
    groupRadio.type = 'radio'
    groupRadio.name = radioName
    groupRadio.value = 'group'
    groupRadio.checked = this.data.mode === 'group'

    groupRadio.addEventListener('change', () => {
      if (groupRadio.checked) {
        this.data.mode = 'group'
        // НЕ сбрасываем xids, просто меняем режим
        this.refreshControls(controls)
      }
    })

    const groupLabel = document.createElement('label')
    groupLabel.textContent = 'Группа товаров'
    groupLabel.prepend(groupRadio)

    controls.appendChild(manualLabel)
    controls.appendChild(groupLabel)

    // 2. Рендерим поля для текущего режима
    this.renderModeFields(controls)

    return controls
  }

  /**
   * Перерисовка блоков полей при смене режима
   */

  private refreshControls (controls: HTMLElement) {
    // Удаляем старые поля
    const oldFields = controls.querySelector('.pc-fields')
    oldFields?.remove()

    // Рендерим заново поля под текущий режим
    this.renderModeFields(controls)
  }

  /**
   * Поля ввода XIDs или выбора группы
   */

  private renderModeFields (controls: HTMLElement) {
    const fieldsContainer = document.createElement('div')
    fieldsContainer.classList.add('pc-fields')

    if (this.data.mode === 'manual') {
      // Поле для XIDs
      const label = document.createElement('label')
      label.textContent = 'Введите артикулы товаров через запятую: '

      const textarea = document.createElement('textarea')
      textarea.rows = 3
      textarea.value = this.data.xids.join(', ')
      textarea.classList.add(`pc-xid-input-${this.blockId}`) // Уникальный класс

      textarea.addEventListener('input', () => {
        const raw = textarea.value.split(',').map((x) => x.trim()).filter(Boolean)
        this.data.xids = raw
        // Принудительно сохраняем изменения
        this.triggerChange()
      })

      label.appendChild(textarea)
      fieldsContainer.appendChild(label)
    }
    else {
      // Group mode
      // рендерим <select> со всеми группами
      const label = document.createElement('label')
      label.textContent = 'Выберите группу товаров: '

      const selectEl = document.createElement('select')
      selectEl.classList.add(`pc-group-select-${this.blockId}`) // Уникальный класс

      // добавим пункт "Выбрать..." для пустого варианта
      const emptyOption = document.createElement('option')
      emptyOption.value = ''
      emptyOption.textContent = '— Выберите группу —'
      selectEl.appendChild(emptyOption)

      this.groupList.forEach((group) => {
        const option = document.createElement('option')
        option.value = `${group.id}`
        option.textContent = group.name
        selectEl.appendChild(option)
      })

      // устанавливаем текущее значение, если есть
      if (this.data.productGroupId) {
        selectEl.value = this.data.productGroupId
      }

      selectEl.addEventListener('change', () => {
        this.data.productGroupId = selectEl.value || undefined
        // Принудительно сохраняем изменения
        this.triggerChange()
      })

      label.appendChild(selectEl)
      fieldsContainer.appendChild(label)

      const viewGroupBtn = document.createElement('button')
      viewGroupBtn.innerText = 'Посмотреть группу'
      viewGroupBtn.type = 'button'
      viewGroupBtn.classList.add(`pc-view-group-btn-${this.blockId}`) // Уникальный класс

      viewGroupBtn.addEventListener('click', () => {
        if (!this.data.productGroupId) {
          alert('Сначала выберите группу')
          return
        }
        // Вызываем колбэк, чтобы форма осуществила переход
        if (this.onViewGroup) {
          this.onViewGroup(this.data.productGroupId)
        }
      })

      fieldsContainer.appendChild(viewGroupBtn)
    }

    controls.appendChild(fieldsContainer)
  }

  /**
   * Уведомляем Editor.js об изменениях в блоке
   */

  private triggerChange () {
    // В Editor.js нет прямого API для триггера изменений, 
    // но данные автоматически сохраняются при вызове save()
    // Можно также использовать dispatchEvent для имитации изменения
    if (this.wrapper) {
      const event = new CustomEvent('change', { bubbles: true })
      this.wrapper.dispatchEvent(event)
    }
  }

  /**
   * Editor.js вызывает этот метод при сохранении данных блока
   */

  save (): ProductCarouselData {
    return {
      mode: this.data.mode,
      xids: this.data.xids,
      productGroupId: this.data.productGroupId,
      title: this.data.title,
    }
  }
}

export default ProductCarousel
